import { IWriteOffActors, readWriteOffActors } from './writeOffActors';
import { STOCK_COMMIT_AT_SEND, type StockCommitMode } from './fireRules';
import { parseHeldOrderFireBlock, type IHeldOrderFireBlock } from './heldOrderFireBlock';
import type { PrepTimeQuote } from "../kitchen/prepTimeQuote";
import type {
  DeliveryPlatform,
  DeliveryProviderKey,
} from "../notifications/Notification";
import { AppliedDiscount, IAppliedDiscount } from "./AppliedDiscount";
import { AppliedTax, IAppliedTax } from "./AppliedTax";
import {
  parseTaxExemption,
  parseWaivedTax,
  type ITaxExemption,
  type IWaivedTax,
} from "./TaxExemption";
import { parseLateAutoGratuity, type ILateAutoGratuity } from "./LateGratuity";
import type { IOrderCardHold } from "./cardHold";
import { parseOrderServiceMode, type OrderServiceMode } from "./serviceMode";
import { isSupportActingAs, parseSupportActor, type SupportActingAs, type SupportActor } from "../support/SupportActor";
import type { IOrderAttribution } from "./OrderAttribution";
import type { ConsentGrantedVia } from "../customer_data/ConsentRecord";
import { activeOrderItems, IOrderItem, OrderItem } from "./OrderItem";
import { type OnlineOrderFeeProgramStamp, type OnlineServiceFeeLine, parseOnlineOrderFeeProgram } from "./onlineServiceFeeOrder";
import { KITCHEN_COMMITTED_STATUSES } from "./stateMachines";
import {
  ExternalOrderSource,
  KitchenState,
  OrderSource,
  OrderSplitStatus,
  OrderState,
  OrderSyncPriority,
  OrderSyncState,
  OrderType,
  ReopenReason,
} from "./types";

/**
 * Void information for order-level voids
 */
export interface IOrderVoidInfo extends IWriteOffActors {
  /** Reason for voiding */
  readonly reason: string;
  /**
   * Who authorized it. The operator's EMPLOYEE id (as a string) when the server
   * resolved one (`readReversalOperatorEmployeeId`); otherwise whatever the
   * writer supplied — usually the caller's uid, the same value as `voidedBy`.
   * `performedBy` is present only in the first case. See `IWriteOffActors` for who DID it.
   */
  readonly authorizedBy: string;
}

/** One modifier, and how many of it a till's void gave back to the shelf. */
export interface IModifierStockReturn {
  readonly modifierId: number;
  readonly quantity: number;
}

/**
 * `voidReturnedModifierStock` off a Firestore document. Undefined when absent;
 * a malformed entry is dropped — it must never become a stock move.
 */
export function parseModifierStockReturns(value: unknown): IModifierStockReturn[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((entry) => {
    const e = entry as { modifierId?: unknown; quantity?: unknown } | null;
    return e && Number.isInteger(e.modifierId) && Number.isInteger(e.quantity) && (e.quantity as number) > 0
      ? [{ modifierId: e.modifierId as number, quantity: e.quantity as number }]
      : [];
  });
}

/**
 * Structured dropoff address components for courier dispatch (DoorDash Drive
 * §5.2 `dropoff_address_components`). When present, `DeliveryDispatchService`
 * uses these directly instead of parsing the freeform `deliveryAddress` string,
 * so Drive create gets non-empty city/state/postalCode and does not 422 on a
 * serviceability check. The checkout flow populates this when it has a
 * structured address; otherwise the dispatcher falls back to best-effort
 * parsing of `deliveryAddress`.
 */
export interface IOrderDeliveryAddressComponents {
  readonly line1?: string;
  readonly line2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postalCode?: string;
  readonly country?: string;
}

/** A GPS position on an order (courier tracking). */
export interface IOrderGeoPoint {
  readonly lat: number;
  readonly lng: number;
}

/** Why an automatic courier dispatch did not happen (Uber Direct spec 2026-10-27 §6.4). */
export interface IOrderCourierDispatchFailure {
  /** Stable machine reason (e.g. `store_unlinked`, `quote_expired`). */
  readonly reason: string;
  readonly detail?: string;
  /** ISO-8601. */
  readonly at: string;
  /** ISO-8601. Set once the staff alert went out; its presence is the per-order dedupe. */
  readonly notifiedAt?: string;
}

/** The fields the courier read-fallback helpers read. */
export type OrderCourierFields = Partial<
  Pick<
    IOrder,
    | 'courierQuoteId'
    | 'courierQuoteExpiresAt'
    | 'driveQuoteId'
    | 'driveQuoteExpiresAt'
    | 'courierLocation'
    | 'dasherLocation'
    | 'merchantCourierCostCents'
    | 'merchantDriveCostCents'
  >
>;

/**
 * The order's locked courier quote, whichever provider took it. Prefers the
 * neutral `courierQuoteId`/`courierQuoteExpiresAt`; falls back to the legacy
 * Drive pair for orders written before the rename (quotes live 15 minutes, so
 * the fallback is retired one release later). The id and expiry are read as a
 * PAIR — never a neutral id with a Drive expiry.
 */
export function orderCourierQuote(
  order: OrderCourierFields,
): { quoteId: string; expiresAt?: string } | undefined {
  if (order.courierQuoteId) {
    return {
      quoteId: order.courierQuoteId,
      ...(order.courierQuoteExpiresAt !== undefined && { expiresAt: order.courierQuoteExpiresAt }),
    };
  }
  if (order.driveQuoteId) {
    return {
      quoteId: order.driveQuoteId,
      ...(order.driveQuoteExpiresAt !== undefined && { expiresAt: order.driveQuoteExpiresAt }),
    };
  }
  return undefined;
}

/** The courier's last known position: `courierLocation`, else the legacy `dasherLocation`. */
export function orderCourierLocation(order: OrderCourierFields): IOrderGeoPoint | undefined {
  return order.courierLocation ?? order.dasherLocation;
}

/** What the courier cost the restaurant, in cents: `merchantCourierCostCents`, else the legacy `merchantDriveCostCents`. */
export function orderMerchantCourierCostCents(order: OrderCourierFields): number | undefined {
  return order.merchantCourierCostCents ?? order.merchantDriveCostCents;
}

/**
 * Dasher/courier vehicle details, stamped from the courier-status webhook
 * (Drive §7.2 `dasher_vehicle_*` / marketplace §8.7 `delivery.dasher.vehicle`).
 * Surfaced on the active-deliveries driver-info card.
 */
export interface IOrderDasherVehicle {
  readonly make?: string;
  readonly model?: string;
  readonly year?: string;
}

/**
 * Pending loyalty-redemption marker (Customer Loyalty v1 — C2, in-restaurant
 * redemption). Stamped on an in-restaurant POS order by the apply-redemption
 * endpoint when the customer chooses to redeem points at the POS. Carries the
 * exact balance owner + point count the POST-CLOSE settle must burn, so the
 * burn happens once, post-charge (at close), against the SAME balance the
 * discount was derived from. The matching customer-facing discount is applied
 * to the check by the POS UI workstream (named 'Loyalty Reward', mirroring the
 * online path), so the earn-on-close basis stays on GROSS spend.
 */
export interface IOrderPendingLoyaltyBurn {
  /** The loyalty identity (balance owner) the points burn from. */
  readonly customerUid: string;
  /** Points to burn at close (> 0 integer). */
  readonly points: number;
}

/**
 * Per-order fire state for ONE course. Keyed by `String(courseId)` because
 * Firestore map keys are strings.
 *
 * The COURSE says how it fires by default (`ICourse.fireMode`); this says what
 * this particular table's copy of it is doing right now. Without it a held
 * course is indistinguishable from a fired one on paper and on the KDS, because
 * both carry their items at `sentToKitchen`.
 */
export interface IOrderCourseState {
  state: "fire" | "hold";
  firedAt?: Date;
  firedBy?: string;
}

/**
 * The audit behind {@link IOrder.smsConsent}: who recorded the transactional
 * SMS opt-in, when, for which number, and the exact wording the guest agreed
 * to. ONE shape for every writer, so reporting sees one kind of consent:
 *
 *   - the online checkout (`source: 'checkout'`, `grantedVia: 'web'`) —
 *     the guest ticked "Text me order updates" themselves;
 *   - the POS till (`source: 'pos'`, `grantedVia: 'staff_entry'`) — staff read
 *     the registered script aloud and ticked the box on the guest's yes
 *     (`apps/pos/lib/utils/order/sms_consent.dart`, which writes these keys).
 *
 * `grantedVia` uses the consent ledger's own vocabulary
 * ({@link ConsentGrantedVia}). `action` is `opt_in` (granted), `declined`
 * (shown and refused) or `cleared` (a different guest was attached to the
 * order, so the previous guest's opt-in no longer applies).
 *
 * Every key is always written — null where it has no value — because order
 * writes MERGE, and a merge of a nested map keeps any key the new value omits.
 * `recordedAt` is an ISO-8601 UTC string (the till's local cache cannot store a
 * Firestore Timestamp inside a map).
 *
 * `smsConsent` stays the ONLY field any sender reads; this record is for
 * audit and reporting.
 */
export interface IOrderSmsConsentRecord {
  readonly granted: boolean;
  readonly action: "opt_in" | "declined" | "cleared";
  readonly grantedVia: Extract<ConsentGrantedVia, "web" | "staff_entry">;
  readonly source: "checkout" | "pos";
  readonly phone: string | null;
  readonly wording: string | null;
  readonly wordingVersion: string | null;
  readonly recordedAt: string;
  readonly recordedByEmployeeId: number | null;
  readonly recordedByEmployeeGuid: string | null;
  readonly recordedByName: string | null;
}

/**
 * What the online checkout's transactional box says. Rendered by
 * `apps/checkout/src/components/CustomerInformation.tsx` FROM this constant and
 * stamped into the order's {@link IOrderSmsConsentRecord} by the API, so the
 * record can never quote words the guest was not shown. Bump the version with
 * any change to the wording — it is filed with the 10DLC campaign.
 */
export const CHECKOUT_SMS_CONSENT_WORDING =
  "Text me order updates (confirmation & tracking) from OrderUp.";
export const CHECKOUT_SMS_CONSENT_WORDING_VERSION = "checkout-v1-2026-09-10";

/**
 * Read a stored {@link IOrderSmsConsentRecord}. Defensive like
 * `parseTaxExemption`: a malformed map reads as absent rather than failing the
 * whole order parse. A Firestore Timestamp `recordedAt` is normalised to the
 * ISO string every writer produces.
 */
export function parseSmsConsentRecord(
  raw: unknown,
): IOrderSmsConsentRecord | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  if (typeof r.granted !== "boolean") return undefined;
  const at = r.recordedAt as { toDate?: () => Date } | string | Date | undefined;
  const recordedAt =
    typeof at === "string"
      ? at
      : at instanceof Date
        ? at.toISOString()
        : typeof at?.toDate === "function"
          ? at.toDate().toISOString()
          : "";
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return {
    granted: r.granted,
    action:
      r.action === "opt_in" || r.action === "declined" || r.action === "cleared"
        ? r.action
        : r.granted
          ? "opt_in"
          : "declined",
    grantedVia: r.grantedVia === "staff_entry" ? "staff_entry" : "web",
    source: r.source === "pos" ? "pos" : "checkout",
    phone: str(r.phone),
    wording: str(r.wording),
    wordingVersion: str(r.wordingVersion),
    recordedAt,
    recordedByEmployeeId:
      typeof r.recordedByEmployeeId === "number" ? r.recordedByEmployeeId : null,
    recordedByEmployeeGuid: str(r.recordedByEmployeeGuid),
    recordedByName: str(r.recordedByName),
  };
}

export interface IOrder {
  readonly docId?: string;
  readonly orderId: number;
  readonly orderNumber: number;
  readonly guid: string;
  readonly restaurantId: number;
  readonly tenantId: number;
  readonly customerId?: string;
  readonly customerName?: string;
  readonly customerPhone?: string;
  readonly customerEmail?: string;
  /**
   * TCPA transactional-SMS consent, captured at checkout and PERSISTED so
   * every downstream notification path can gate on it.
   *
   * Before this field existed, `smsConsent` was read off the checkout request,
   * handed to the order-receipt sender, and discarded — so the courier
   * milestone, order-status lifecycle and appointment-reminder senders had
   * nothing to check and fell back to "a phone number is present", texting
   * customers who never agreed to be texted.
   *
   * Absent/`false` means NO marketing or lifecycle SMS. It does not suppress
   * the message entirely — those paths fall back to email/push.
   */
  readonly smsConsent?: boolean;
  /** The audit behind {@link smsConsent}. See {@link IOrderSmsConsentRecord}. */
  readonly smsConsentRecord?: IOrderSmsConsentRecord;
  readonly cartId?: number;
  readonly items: IOrderItem[];
  readonly tableId?: string;
  readonly activeSeatCount?: number;
  /**
   * FIRE/HOLD per course for THIS order, keyed by `String(courseId)`. Absent on
   * an order that has never been coursed.
   */
  readonly courseStates?: Record<string, IOrderCourseState>;
  readonly type: OrderType;
  readonly orderLevelDiscounts: IAppliedDiscount[];
  readonly appliedTaxes: IAppliedTax[];
  /**
   * Order-level tax exemption — the AUTHORIZATION RECORD (who asked, which
   * manager approved, the purchaser, the certificate photos, the history).
   * SERVER-OWNED: written only by the API (`/orders/:guid/tax-exemption`, gated
   * on `manager_tax_exempt`) and refused to every client by `firestore.rules`.
   * `active: true` means the POS tax engine charges no tax on this order.
   * See docs/TAX_EXEMPTION_ARCHITECTURE.md.
   */
  readonly taxExemption?: ITaxExemption;
  /**
   * What the tax WOULD have been while {@link taxExemption} is active —
   * computed by the POS tax engine in place of `appliedTaxes`; `null`/absent
   * otherwise. The warehouse reads it for exempt sales and "tax not collected".
   */
  readonly waivedTax?: IWaivedTax;
  /**
   * Late auto-gratuity — the SERVER-OWNED projection of a gratuity that applied
   * only after every check held money: owed / collected / waived, the rule it was
   * frozen at, the last event. Written only by the API (`LateGratuityService`)
   * and refused to every client by `firestore.rules`; the POS reads it and never
   * sends it. See docs/LATE_AUTO_GRATUITY_ARCHITECTURE.md.
   */
  readonly lateAutoGratuity?: ILateAutoGratuity;
  /**
   * Card hold (pre-auth card on file) — the SERVER-OWNED, token-free projection
   * of `card_holds/{holdId}` (docs/CARD_HOLDS.md). Written only by the API
   * (`CardHoldService`) and refused to every client by `firestore.rules`; the
   * POS reads it and never sends it. Absent = no hold was ever placed.
   *
   * TYPE ONLY: the `Order` class does not carry it at runtime. Readers parse it
   * from the raw document with `parseOrderCardHold` (the allow-list), which
   * keeps that parser out of every web bundle that builds an `Order`.
   */
  readonly cardHold?: IOrderCardHold;
  /**
   * Who closed the order when it was not a person: `auto_close` (the batch
   * cutoff closed a held tab). Server-owned, like `cardHold`. Absent on every
   * order a person closed.
   */
  readonly closedBy?: string;
  /** Why a server close happened: `walkout`. Server-owned; absent otherwise. */
  readonly closeReason?: string;
  readonly scheduledTime?: Date;
  /**
   * When a held `scheduled` order should fire to the kitchen:
   * `scheduledTime − prepLeadMinutes`, clamped `>= createdAt`. Persisted at
   * creation so the unified scheduled-order activator can query
   * `orderState=='scheduled' && fireAt<=now`. Absent for ASAP orders.
   * See docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md §6.
   */
  readonly fireAt?: Date;
  /**
   * Set while the activator is holding this order back at its `fireAt` —
   * a pending till line still owes a size or a required choice. Server-owned
   * and derived; null/absent once the order fires. See `heldOrderFireBlock`.
   */
  readonly fireBlocked?: IHeldOrderFireBlock | null;
  /**
   * WHO takes this order's item stock off the shelf. `'atSend'` — stamped by a
   * till build that only RESERVES stock locally — means the server does it at
   * kitchen send/fire, once per line. Absent on a till order means the till
   * that rang it in already decremented each line when it was ADDED (every
   * till before 2026-09-25), so the server must not count it again. Orders
   * from every other source are always the server's. See
   * `serverTakesStockAtSend` (fireRules).
   */
  readonly stockCommit?: StockCommitMode;
  /**
   * When the kitchen PROMISED the food — written once, at the moment we commit
   * to a time (auto-accept, manual marketplace accept, first-party auto
   * create). Both order autopilots mark ready at this, not at a time they
   * recompute from settings. See docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md §5.
   *
   * ⚠️ The POS `Order.toMap()` must carry it, or a till write-back deletes it.
   */
  readonly promisedReadyAt?: Date;
  /** Why the order got its prep time (`quotePrepTime`'s breakdown). */
  readonly prepQuote?: PrepTimeQuote;
  readonly deliveryAddress?: string;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly createdByUserId: string;
  readonly tabName?: string;
  /**
   * POS service mode the order was opened in (`table` | `tab` | `takeout`).
   * Optional: absent = a legacy order, or one written while
   * `initiative.service_modes` is off. See `./serviceMode.ts`.
   */
  readonly serviceMode?: OrderServiceMode;
  readonly customerNotes?: string;
  readonly internalNotes?: string;
  readonly synced: boolean;
  readonly canceledAt?: Date;
  /**
   * The CALLER's Firebase uid — on a till that is the DEVICE, never a person.
   * Kept with that meaning (old readers depend on it); WHO cancelled it is the
   * employee in `voidInfo.performedBy` / `authorizedBy`
   * (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Who did it).
   */
  readonly canceledBy?: string;
  readonly voidedAt?: Date;
  /** The CALLER's Firebase uid, like `canceledBy` — the employee is in `voidInfo`. */
  readonly voidedBy?: string;

  // Three-axis order state
  /** Business lifecycle state */
  readonly orderState: OrderState;
  /** Kitchen production state — derived from per-item kitchenStatus values */
  readonly kitchenState: KitchenState;
  /** When the order was closed (set by composite close check) */
  readonly closedAt?: Date;
  /**
   * The reopen stamp — set when this order went `closed → open` because a
   * payment on it was voided (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Reopen on
   * void). All three together or none; a manager reopen writes none of them.
   */
  readonly reopenedAt?: Date;
  readonly reopenReason?: ReopenReason;
  readonly reopenedByPaymentGuid?: string;
  /** Void info for order-level voids (set when orderState → 'voided') */
  readonly voidInfo?: IOrderVoidInfo;
  /**
   * The modifier stock a TILL's void gave back, written by the till in the
   * same write as the void. The undo of the void (restore-void) takes back
   * exactly this on the till, and the server clears it when it restores the
   * order. Absent on an API void, which gives no modifier stock back.
   */
  readonly voidReturnedModifierStock?: readonly IModifierStockReturn[];
  /** Whether the order is overdue */
  readonly isOverdue?: boolean;

  // Offline-first fields
  readonly syncState: OrderSyncState;
  readonly syncPriority: OrderSyncPriority;
  readonly lastSyncAttempt?: Date;
  readonly syncRetryCount: number;
  readonly startedAt?: Date;
  readonly sentToKitchenAt?: Date;
  readonly requiresFirestoreSync: boolean;
  readonly isLocalOnly: boolean;

  // Employee tracking (harmonized with POS)
  readonly createdByEmployeeId?: number;
  readonly modifiedByEmployeeId?: number;
  readonly sentToKitchenByEmployeeId?: number;
  readonly completedByEmployeeId?: number;
  readonly assignedEmployeeId?: number;
  readonly assignedDeviceId?: string;

  /**
   * Set iff this document was written by an OrderUp support session. When it is
   * present, `createdByEmployeeId` is deliberately ABSENT — the operator is not
   * on the merchant's roster and inventing an id for them is what would reach
   * payroll. See `packages/models/src/support/SupportActor.ts`.
   *
   * Every field but the label is pinned to the caller's own token by
   * `firestore.rules`, so this cannot name anyone other than whoever made the
   * write.
   */
  readonly supportActor?: SupportActor;
  /**
   * WHO OPENED it under OrderUp Console act-as — the `supportActor.actingAs` of
   * the CREATING write, frozen. `supportActor` is restamped by every support
   * write (the last toucher); this never changes after create
   * (`firestore.rules` `supportOpenerUnchanged`). Reports read it to file a
   * job-mode ticket under "OrderUp Support".
   */
  readonly supportOpener?: SupportActingAs;

  // Segment-aware tip pooling: content-derived `restaurantGuid:date:window`
  // segment id, resolved + stamped by the POS at order creation.
  readonly segmentGuid?: string;

  /**
   * Denormalized business GUIDs of the owning tenant / restaurant, stamped onto
   * the order doc at creation. The Firestore path uses opaque docIds, but every
   * cross-service consumer that resolves the order back to its tenant/restaurant
   * (notably apps/functions notification triggers, which forward to the ingest
   * API that resolves BY GUID) needs the GUIDs on the doc — a docId can't be
   * resolved by those consumers and yields a 404. Optional only for legacy/edge
   * docs written before this field existed; all create paths now stamp them.
   */
  readonly tenantGuid?: string;
  readonly restaurantGuid?: string;

  /**
   * WHICH BRAND this order was placed with — the marketplace-facing identity
   * within the restaurant (`packages/models/src/brands/Brand.ts`).
   *
   * 🔴 ROUTING ALREADY WORKS WITHOUT THIS; ATTRIBUTION DOES NOT. An aggregator
   * order reaches the right restaurant via `partner_store_id` → integration →
   * tenant/restaurant. But a ghost kitchen runs several brands out of ONE
   * kitchen and one order stream, so without this field their orders arrive
   * undifferentiated: the expo ticket cannot say which brand to bag it under,
   * sales reporting cannot split by brand, and Books gets no brand dimension.
   *
   * Absent for first-party (POS, web) orders and for any restaurant that never
   * adopted a second brand — a single-brand venue's orders are unambiguous, so
   * nothing downstream may REQUIRE this field. It is additive: readers that do
   * not know about brands are unaffected.
   */
  readonly brandGuid?: string;

  /**
   * The brand's name AS THE MARKETPLACE DISPLAYED IT when this order was
   * placed, denormalized beside `brandGuid`.
   *
   * 🔴 DENORMALIZED ON PURPOSE, AND DELIBERATELY NOT KEPT IN SYNC. Two reasons:
   *
   *   1. Every surface that shows a ticket needs a NAME, not a guid — the POS
   *      expo screen, the KDS, the order list, a printed label. Resolving a
   *      brand per ticket is a lookup on the hottest path in the kitchen, for a
   *      string that cannot change for an order already placed.
   *   2. A rename must NOT rewrite history. If "Ronald's Haitian Cuisine"
   *      becomes something else next month, the order the diner placed, the
   *      receipt they hold and the label the kitchen printed all still say what
   *      they said. Joining live to the brand row would silently restate them.
   *
   * Absent for first-party orders and for restaurants that never adopted a
   * second brand — a single-brand venue's tickets need no disambiguation.
   */
  readonly brandName?: string;

  // Order source tracking
  readonly orderSource?: OrderSource;
  /**
   * Where a printed guest code sent this guest — see {@link IOrderAttribution}.
   * Orthogonal to `orderSource`: that says which SURFACE created the order,
   * this says which printed CODE, if any, led the guest to it.
   */
  readonly attribution?: IOrderAttribution;
  /**
   * MARKETPLACE external order id. For orders that originated on a
   * third-party marketplace (Uber Eats / DoorDash Marketplace /
   * Grubhub), this is the provider's native order id used to map
   * inbound webhooks (status, cancellation, modification) back to OUR
   * Order document.
   *
   * Distinct from `externalDeliveryId` (below): `externalOrderId` is
   * the *marketplace* id (one provider, one ingestion path), while
   * `externalDeliveryId` is the *Drive/courier* id (different
   * provider possible — e.g. a marketplace Uber order whose courier
   * is sourced via DoorDash Drive).
   */
  readonly externalOrderId?: string;
  readonly externalSource?: ExternalOrderSource;
  /**
   * DRIVE/COURIER external delivery id. Set by
   * `DeliveryDispatchService.dispatchIfNeeded` after a successful
   * `ICourierProvider.createDelivery` call on order accept. This is the
   * provider's id for the COURIER pickup-and-drop (e.g. DoorDash Drive
   * `external_delivery_id`) — NOT the marketplace order id.
   *
   * Doubles as the idempotency key for re-dispatch: when present, the
   * dispatcher returns `{ dispatched: false, reason:
   * 'already-dispatched' }` and never re-issues the create call.
   */
  readonly externalDeliveryId?: string;
  /**
   * The provider whose courier currently owns the in-flight delivery
   * referenced by `externalDeliveryId`. Persisted alongside the id so
   * any later webhook / status-poll / cancel can resolve the correct
   * provider adapter from the registry without re-reading
   * `restaurant.settings.courierProvider` (which the merchant may
   * have changed after the dispatch).
   *
   * ⚠️ ADAPTER IDENTITY, NOT ATTRIBUTION (D1). This is who we DISPATCH
   * THROUGH, which is exactly what the paragraph above describes — it is fed
   * from `restaurant.settings.courierProvider`, whose own schema comment calls
   * it "unambiguously the adapter". It was typed `DeliveryPlatform` (the
   * MARKETPLACE axis, which deliberately excludes `'kitchenhub'`), so the one
   * value an aggregator dispatch actually writes was the one value the type
   * forbade. Nothing caught it because the write goes through
   * `updateOrderMetadataByGuid(..., metadata: Record<string, any>)`.
   */
  readonly courierProvider?: DeliveryProviderKey;
  /**
   * Customer-facing delivery fee charged at checkout, in CENTS. Computed
   * server-side from `restaurant.settings.feesAndCharges.deliveryCharge`
   * (DeliveryChargeConfig) — NOT the live Drive quote. Persisted so the
   * charged amount is auditable independent of later config changes.
   */
  readonly customerDeliveryFeeCents?: number;
  /** DoorDash Drive quote locked at preview time + its expiry, threaded to dispatch
   *  so the courier is created at the exact fee/coverage the customer saw. */
  readonly driveQuoteId?: string;
  readonly driveQuoteExpiresAt?: string;
  /**
   * The merchant's DoorDash Drive courier cost in CENTS, taken from
   * `CourierQuote.feeCents` at the settled economic event
   * (DASHER_CONFIRMED). Captured server-side for P&L / GL only — it is
   * NEVER charged to the customer and never sent to the browser.
   *
   * @deprecated read-fallback only — new writers write `merchantCourierCostCents`.
   */
  readonly merchantDriveCostCents?: number;

  // ── Provider-neutral courier fields (Uber Direct spec 2026-10-27 §6.4) ──
  // Written by EVERY courier provider. The Drive-named fields above are read as
  // a fallback for one release through `orderCourierQuote` /
  // `orderCourierLocation`, then deleted; writers write only these names.
  /** Courier quote locked at preview time, threaded to dispatch. */
  readonly courierQuoteId?: string;
  /** ISO-8601 expiry of `courierQuoteId`. */
  readonly courierQuoteExpiresAt?: string;
  /**
   * What the courier costs the RESTAURANT, in cents (billed to the location —
   * spec §9). Server-side only; never charged to the guest by this field.
   */
  readonly merchantCourierCostCents?: number;
  /** Last known courier GPS position. */
  readonly courierLocation?: IOrderGeoPoint;
  /** Why an automatic courier dispatch did not happen, when it did not. */
  readonly courierDispatchFailure?: IOrderCourierDispatchFailure;

  // ── Live courier tracking (Drive §7.2) ──────────────────────────────
  // Stamped onto the order by the courier-status webhook (via the non-order
  // orchestrator) and at dispatch. Read-only surface for the management/POS
  // active-deliveries view. WITHOUT these declared here, Order.fromJSON drops
  // them and they never reach any client UI.
  /** Raw provider courier status (e.g. "DASHER_CONFIRMED"); normalize for display. */
  readonly courierStatus?: string;
  /**
   * Last known Dasher GPS position from the §7.2 webhook.
   * @deprecated read-fallback only — new writers write `courierLocation`.
   */
  readonly dasherLocation?: { lat: number; lng: number };
  /** Estimated dropoff time (ISO-8601) from the courier. */
  readonly eta?: string;
  /** When `courierStatus` was last updated (ISO-8601). */
  readonly courierStatusUpdatedAt?: string;
  /** Provider tracking URL (DoorDash Drive `tracking_url`), persisted at dispatch. */
  readonly trackingUrl?: string;
  /** Dasher display name (from the §7.2 webhook), when assigned. */
  readonly courierName?: string;
  /** Dasher contact phone (from the §7.2 webhook), when assigned. */
  readonly courierPhone?: string;
  /**
   * PIN for the courier's anonymized phone line (Uber marketplace
   * `delivery_partner.contact.phone.pin_code`) — required to connect after
   * dialing `courierPhone`.
   */
  readonly courierPhonePin?: string;
  /** Dasher vehicle (make/model/year) from the courier-status webhook. */
  readonly dasherVehicle?: IOrderDasherVehicle;
  /**
   * Structured dropoff address components for courier dispatch (Drive §5.2).
   * When present the dispatcher uses these verbatim; otherwise it best-effort
   * parses `deliveryAddress`. See {@link IOrderDeliveryAddressComponents}.
   */
  readonly deliveryAddressComponents?: IOrderDeliveryAddressComponents;

  // ── Test-order mode + customer milestone-SMS dedup ──────────────────────
  /**
   * Marks a synthetic management "test order". Set by
   * `OrderService.createTestDeliveryOrder`. Drives the board's TEST badge and
   * suppresses customer milestone SMS (we never text a real number for a test
   * order). WITHOUT this declared here, `Order.fromJSON` drops it and neither
   * the board nor the SMS-suppression gate ever sees it.
   */
  readonly isTest?: boolean;
  /**
   * Canonical courier milestones a customer SMS has ALREADY been sent for
   * (e.g. `['courier_assigned','picked_up']`). The courier-status webhook fires
   * on every AOR ping — all collapsing onto the same canonical phase — so this
   * persisted set is the fire-once dedup key for milestone SMS. Stamped in the
   * SAME metadata write that updates `courierStatus`.
   */
  readonly notifiedCourierMilestones?: string[];

  // ── Digital receipt dispatch (Customer Loyalty v1 — C3) ─────────────────
  /**
   * When the customer order-confirmation receipt (email/SMS) was sent. The
   * fire-once dedup marker shared by the online checkout send path and the
   * in-restaurant close cascade — when set, a re-delivered close never
   * re-sends. Mirrors the persistence pattern of `closedAt` (a Date that
   * round-trips through a Firestore Timestamp in `fromJSON`).
   */
  readonly receiptSentAt?: Date;
  /**
   * Which channels the receipt was actually dispatched on (e.g. `['email']`,
   * `['email','sms']`). Mirrors `notifiedCourierMilestones` (a string[]).
   */
  readonly receiptChannels?: string[];

  // ── Loyalty redemption pending burn (Customer Loyalty v1 — C2) ───────────
  /**
   * In-restaurant redemption marker. When present, the post-close loyalty
   * settle (`/admin/loyalty/_internal/settle-pos-order`) burns exactly
   * `points` from `customerUid` (idempotent `${guid}_burn`), AFTER the earn
   * block so earning stays on gross spend. Set by the apply-redemption
   * endpoint; a re-apply REPLACES it (never stacks). Absent on orders with no
   * POS redemption. See {@link IOrderPendingLoyaltyBurn}.
   */
  readonly pendingLoyaltyBurn?: IOrderPendingLoyaltyBurn;

  // Check splitting & transfer fields
  /** References to check documents — accepts checkId (number) or checkGuid (string) */
  readonly checks: (string | number)[];
  readonly activeCheckCount?: number;
  /** Total service charges across all checks (denormalized by CheckCascadeService) */
  readonly totalServiceCharges: number;
  /** Total service tax across all checks (denormalized by CheckCascadeService) */
  readonly totalServiceTax: number;
  readonly splitStatus: OrderSplitStatus;
  readonly hasReceivedTransfers?: boolean;
  readonly hasOutgoingTransfers?: boolean;

  /** Sub-number for cancelled/voided orders with items (e.g., 1 in #5.1) */
  readonly cancellationSubNumber?: number;

  // ── Uber Eats marketplace-specific ingest fields ────────────────────────
  /**
   * Customer-side anonymized-line PIN (Uber Eats
   * `customers[].contact.phone.pin_code`). Distinct from `courierPhonePin`
   * (which is the courier's PIN). Required to reach the customer on the
   * Uber-masked number when calling back.
   */
  readonly customerPhonePin?: string;
  /**
   * Whether the customer requested cutlery / single-use items
   * (`cutlery_requested` / `single_use_items_request` on the Uber order).
   */
  readonly cutleryRequested?: boolean;
  /**
   * Courier dropoff instructions from the customer (`deliveries[0].delivery_notes`).
   * Distinct from `customerNotes` (which is the cart-level special instruction).
   */
  readonly deliveryNotes?: string;
  /**
   * Marketplace facilitator tax in CENTS (Uber
   * `payment.tax_reporting.marketplace_facilitator_tax`). Separate from the
   * merchant-collected `tax` in order totals; needed for tax-reporting.
   */
  readonly marketplaceFacilitatorTaxCents?: number;
  /** The Online Service Fee line, when one was charged (its money is in totalServiceCharges/totalServiceTax). */
  readonly onlineServiceFee?: OnlineServiceFeeLine;
  /**
   * SERVER-OWNED: stamped by checkout when this order was placed under
   * OrderUp's per-order fee program (kill switch on). The ingest bills ONLY an
   * order that carries it — billing follows the checkout, never the clock at
   * close. See docs/ONLINE_SERVICE_FEE.md.
   */
  readonly onlineOrderFeeProgram?: OnlineOrderFeeProgramStamp;
}

export interface IOrderData {
  docId?: string;
  orderId: number;
  orderNumber?: number;
  guid: string;
  restaurantId: number;
  tenantId: number;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  /** TCPA transactional-SMS consent captured at checkout. See {@link IOrder.smsConsent}. */
  smsConsent?: boolean;
  /** See {@link IOrder.smsConsentRecord}. */
  smsConsentRecord?: IOrderSmsConsentRecord;
  cartId?: number;
  items?: OrderItem[];
  tableId?: string;
  activeSeatCount?: number;
  /** FIRE/HOLD per course for this order. See {@link IOrder.courseStates}. */
  courseStates?: Record<string, IOrderCourseState>;
  type?: OrderType;
  /**
   * The CART's fulfilment, carried onto the order for display. `type` above is
   * the KITCHEN's view of the same fact; the two are mapped explicitly in
   * `orderTypeForFulfilment` (`ORDER_TYPE_BY_FULFILMENT`), never derived from each other.
   * Kept in step with `CartOrderType` and with `OrderDataSchema.orderType` —
   * leaving this narrow while the schema widened put the TS type and the
   * runtime validator into disagreement, which `tsc` caught here and zod
   * caught at `orderService.ts:810`.
   */
  orderType?: "pickup" | "delivery" | "dineIn";
  orderLevelDiscounts?: AppliedDiscount[];
  appliedTaxes?: AppliedTax[];
  // Tax exemption (server-owned record) + the tax it waived — see IOrder JSDoc.
  taxExemption?: ITaxExemption;
  waivedTax?: IWaivedTax;
  // Late auto-gratuity (server-owned projection) — see IOrder JSDoc.
  lateAutoGratuity?: ILateAutoGratuity;
  // Card hold projection + server close stamp (server-owned) — see IOrder JSDoc.
  cardHold?: IOrderCardHold;
  closedBy?: string;
  closeReason?: string;
  // Calculated totals for order summary
  subtotal?: number;
  tax?: number;
  total?: number;
  // Employee tracking (harmonized with POS)
  createdByEmployeeId?: number;
  modifiedByEmployeeId?: number;
  sentToKitchenByEmployeeId?: number;
  completedByEmployeeId?: number;
  assignedEmployeeId?: number;
  assignedDeviceId?: string;

  /**
   * Set iff this document was written by an OrderUp support session. When it is
   * present, `createdByEmployeeId` is deliberately ABSENT — the operator is not
   * on the merchant's roster and inventing an id for them is what would reach
   * payroll. See `packages/models/src/support/SupportActor.ts`.
   *
   * Every field but the label is pinned to the caller's own token by
   * `firestore.rules`, so this cannot name anyone other than whoever made the
   * write.
   */
  supportActor?: SupportActor;
  supportOpener?: SupportActingAs;
  // Segment-aware tip pooling
  segmentGuid?: string;
  /**
   * Denormalized business GUIDs of the owning tenant / restaurant — see
   * IOrder.tenantGuid JSDoc. Stamped at creation so notification triggers (which
   * forward to the ingest API resolving BY GUID) never fall back to docIds.
   */
  tenantGuid?: string;
  restaurantGuid?: string;
  /** See `IOrder.brandGuid`. */
  brandGuid?: string;
  /** See `IOrder.brandName`. */
  brandName?: string;
  /**
   * Present only when the server's offline projection re-minted this order's
   * `orderId` because the id a till assigned offline was already held by a
   * different order (`OrderIdReassignmentSchema` in @order-up/schemas).
   */
  orderIdReassignment?: {
    fromOrderId: number;
    conflictingOrderGuids: string[];
    reason: 'offline_projection_collision';
    reassignedAt: string;
  };
  // Order source tracking
  orderSource?: OrderSource;
  /** See IOrder.attribution JSDoc. */
  attribution?: IOrderAttribution;
  /** MARKETPLACE external order id — see IOrder.externalOrderId JSDoc. */
  externalOrderId?: string;
  externalSource?: ExternalOrderSource;
  /** DRIVE/COURIER external delivery id — see IOrder.externalDeliveryId JSDoc. */
  externalDeliveryId?: string;
  /** Courier provider for in-flight Drive delivery — see IOrder.courierProvider JSDoc. */
  courierProvider?: DeliveryProviderKey;
  /** Customer-facing delivery fee charged at checkout, in cents — see IOrder.customerDeliveryFeeCents. */
  customerDeliveryFeeCents?: number;
  driveQuoteId?: string;
  driveQuoteExpiresAt?: string;
  /** Merchant Drive courier cost in cents (CourierQuote.feeCents) — see IOrder.merchantDriveCostCents. */
  merchantDriveCostCents?: number;
  // Provider-neutral courier fields — see IOrder JSDoc.
  courierQuoteId?: string;
  courierQuoteExpiresAt?: string;
  merchantCourierCostCents?: number;
  courierLocation?: IOrderGeoPoint;
  courierDispatchFailure?: IOrderCourierDispatchFailure;
  // Live courier tracking (Drive §7.2) — see IOrder JSDoc.
  courierStatus?: string;
  dasherLocation?: { lat: number; lng: number };
  eta?: string;
  courierStatusUpdatedAt?: string;
  trackingUrl?: string;
  courierName?: string;
  courierPhone?: string;
  courierPhonePin?: string;
  dasherVehicle?: IOrderDasherVehicle;
  deliveryAddressComponents?: IOrderDeliveryAddressComponents;
  // Test-order mode + customer milestone-SMS dedup — see IOrder JSDoc.
  isTest?: boolean;
  notifiedCourierMilestones?: string[];
  // Digital receipt dispatch (C3) — see IOrder JSDoc.
  receiptSentAt?: Date;
  receiptChannels?: string[];
  // Loyalty redemption pending burn (C2) — see IOrder JSDoc.
  pendingLoyaltyBurn?: IOrderPendingLoyaltyBurn;
  // Check splitting & transfer fields
  checks?: (string | number)[];
  activeCheckCount?: number;
  totalServiceCharges?: number;
  totalServiceTax?: number;
  splitStatus?: OrderSplitStatus;
  hasReceivedTransfers?: boolean;
  hasOutgoingTransfers?: boolean;
  // Uber Eats marketplace-specific ingest fields — see IOrder JSDoc.
  customerPhonePin?: string;
  cutleryRequested?: boolean;
  deliveryNotes?: string;
  marketplaceFacilitatorTaxCents?: number;
  onlineServiceFee?: OnlineServiceFeeLine;
  /** SERVER-OWNED per-order fee program stamp — see IOrder.onlineOrderFeeProgram. */
  onlineOrderFeeProgram?: OnlineOrderFeeProgramStamp;
  scheduledTime?: Date;
  /** Kitchen fire time for a held scheduled order (scheduledTime − prep lead). */
  fireAt?: Date;
  /** See `IOrder.fireBlocked`. */
  fireBlocked?: IHeldOrderFireBlock | null;
  stockCommit?: StockCommitMode;
  promisedReadyAt?: Date;
  prepQuote?: PrepTimeQuote;
  deliveryAddress?: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdByUserId: string;
  tabName?: string;
  /** See {@link IOrder.serviceMode}. */
  serviceMode?: OrderServiceMode;
  customerNotes?: string;
  internalNotes?: string;
  synced?: boolean;
  canceledAt?: Date;
  canceledBy?: string;
  voidedAt?: Date;
  voidedBy?: string;
  // Three-axis order state
  orderState?: OrderState;
  kitchenState?: KitchenState;
  closedAt?: Date;
  reopenedAt?: Date;
  reopenReason?: ReopenReason;
  reopenedByPaymentGuid?: string;
  voidInfo?: IOrderVoidInfo;
  voidReturnedModifierStock?: readonly IModifierStockReturn[];
  isOverdue?: boolean;
  syncState?: OrderSyncState;
  syncPriority?: OrderSyncPriority;
  lastSyncAttempt?: Date;
  syncRetryCount?: number;
  startedAt?: Date;
  sentToKitchenAt?: Date;
  requiresFirestoreSync?: boolean;
  isLocalOnly?: boolean;
  cancellationSubNumber?: number;
}

/**
 * The daily number as stored, read without inventing one.
 *
 * `0` is the codebase's "no number held" value — an unnumbered draft, or (POS
 * #312) an empty draft that was cancelled and gave its number back to the
 * day's pool. It is a real answer, so it is KEPT. The permanent `orderId` is
 * only a stand-in for a legacy row that never had the field at all.
 *
 * This read `Number(raw) || orderId`, and `0 || orderId` is `orderId`: every
 * relinquished shell came back from Firestore wearing its permanent id as a
 * daily number — `#1234` on the cancelled-orders report, and on a young
 * restaurant a permanent id small enough to be the same `#35` a live ticket
 * carries, which is the duplicate the relinquish exists to remove.
 */
function parseOrderNumber(raw: unknown, orderId: number): number {
  if (raw === undefined || raw === null || raw === "") return orderId;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : orderId;
}

/**
 * The order states that mean "finished — no further items".
 *
 * ONE list, exported, because this rule exists in four places across three
 * languages (TypeScript here, Dart on the till, the Firestore rules, and the
 * lifecycle doc) and the TypeScript copy silently disagreed with the other
 * three by omitting `transferred`. See {@link Order.isTerminal}.
 */
export const ORDER_TERMINAL_STATES = [
  'closed',
  'cancelled',
  'voided',
  'transferred',
] as const;

export class Order implements IOrder {
  readonly docId?: string;
  readonly orderId: number;
  /**
   * Daily sequential number the guest and the kitchen see. Unique among the
   * orders that HOLD a number on one business day; `0` holds none (see
   * `parseOrderNumber`). An operator-facing LABEL — identity is `guid`.
   */
  readonly orderNumber: number;
  readonly guid: string;
  readonly restaurantId: number;
  readonly tenantId: number;
  readonly customerId?: string;
  readonly customerName?: string;
  readonly customerPhone?: string;
  readonly customerEmail?: string;
  /** TCPA transactional-SMS consent captured at checkout. See {@link IOrder.smsConsent}. */
  readonly smsConsent?: boolean;
  /** See {@link IOrder.smsConsentRecord}. */
  readonly smsConsentRecord?: IOrderSmsConsentRecord;
  readonly cartId?: number;
  readonly items: OrderItem[];
  readonly tableId?: string;
  readonly activeSeatCount?: number;
  /** FIRE/HOLD per course for this order. See {@link IOrder.courseStates}. */
  readonly courseStates?: Record<string, IOrderCourseState>;
  readonly type: OrderType;
  readonly orderLevelDiscounts: AppliedDiscount[];
  readonly appliedTaxes: AppliedTax[];
  // Tax exemption (server-owned record) + the tax it waived — see IOrder JSDoc.
  readonly taxExemption?: ITaxExemption;
  readonly waivedTax?: IWaivedTax;
  // Late auto-gratuity (server-owned projection) — see IOrder JSDoc.
  readonly lateAutoGratuity?: ILateAutoGratuity;
  readonly scheduledTime?: Date;
  /** Kitchen fire time for a held scheduled order (scheduledTime − prep lead). */
  readonly fireAt?: Date;
  readonly fireBlocked?: IHeldOrderFireBlock | null;
  readonly stockCommit?: StockCommitMode;
  readonly promisedReadyAt?: Date;
  readonly prepQuote?: PrepTimeQuote;
  readonly deliveryAddress?: string;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly createdByUserId: string;
  readonly tabName?: string;
  /**
   * POS service mode the order was opened in (`table` | `tab` | `takeout`).
   * Optional: absent = a legacy order, or one written while
   * `initiative.service_modes` is off. See `./serviceMode.ts`.
   */
  readonly serviceMode?: OrderServiceMode;
  readonly customerNotes?: string;
  readonly internalNotes?: string;
  readonly synced: boolean;
  readonly canceledAt?: Date;
  readonly canceledBy?: string;
  readonly voidedAt?: Date;
  readonly voidedBy?: string;

  // Three-axis order state
  readonly orderState: OrderState;
  readonly kitchenState: KitchenState;
  readonly closedAt?: Date;
  readonly reopenedAt?: Date;
  readonly reopenReason?: ReopenReason;
  readonly reopenedByPaymentGuid?: string;
  readonly voidInfo?: IOrderVoidInfo;
  readonly voidReturnedModifierStock?: readonly IModifierStockReturn[];
  readonly isOverdue?: boolean;

  // Offline-first fields
  readonly syncState: OrderSyncState;
  readonly syncPriority: OrderSyncPriority;
  readonly lastSyncAttempt?: Date;
  readonly syncRetryCount: number;
  readonly startedAt?: Date;
  readonly sentToKitchenAt?: Date;
  readonly requiresFirestoreSync: boolean;
  readonly isLocalOnly: boolean;

  // Employee tracking (harmonized with POS)
  readonly createdByEmployeeId?: number;
  readonly modifiedByEmployeeId?: number;
  readonly sentToKitchenByEmployeeId?: number;
  readonly completedByEmployeeId?: number;
  readonly assignedEmployeeId?: number;
  readonly assignedDeviceId?: string;

  /**
   * Set iff this document was written by an OrderUp support session. When it is
   * present, `createdByEmployeeId` is deliberately ABSENT — the operator is not
   * on the merchant's roster and inventing an id for them is what would reach
   * payroll. See `packages/models/src/support/SupportActor.ts`.
   *
   * Every field but the label is pinned to the caller's own token by
   * `firestore.rules`, so this cannot name anyone other than whoever made the
   * write.
   */
  readonly supportActor?: SupportActor;
  readonly supportOpener?: SupportActingAs;

  // Segment-aware tip pooling: content-derived `restaurantGuid:date:window`
  // segment id, resolved + stamped by the POS at order creation.
  readonly segmentGuid?: string;

  // Denormalized owning tenant/restaurant GUIDs — see IOrder JSDoc.
  readonly tenantGuid?: string;
  readonly restaurantGuid?: string;
  /** Which BRAND the order was placed with — see IOrder.brandGuid. */
  readonly brandGuid?: string;
  /** That brand's name as displayed at order time — see IOrder.brandName. */
  readonly brandName?: string;

  // Order source tracking
  readonly orderSource?: OrderSource;
  /** See IOrder.attribution JSDoc. */
  readonly attribution?: IOrderAttribution;
  readonly externalOrderId?: string;
  readonly externalSource?: ExternalOrderSource;
  readonly externalDeliveryId?: string;
  readonly courierProvider?: DeliveryProviderKey;
  readonly customerDeliveryFeeCents?: number;
  /** DoorDash Drive quote locked at preview time + its expiry, threaded to dispatch
   *  so the courier is created at the exact fee/coverage the customer saw. */
  readonly driveQuoteId?: string;
  readonly driveQuoteExpiresAt?: string;
  readonly merchantDriveCostCents?: number;
  readonly courierQuoteId?: string;
  readonly courierQuoteExpiresAt?: string;
  readonly merchantCourierCostCents?: number;
  readonly courierLocation?: IOrderGeoPoint;
  readonly courierDispatchFailure?: IOrderCourierDispatchFailure;
  // Live courier tracking (Drive §7.2) — see IOrder JSDoc.
  readonly courierStatus?: string;
  readonly dasherLocation?: { lat: number; lng: number };
  readonly eta?: string;
  readonly courierStatusUpdatedAt?: string;
  readonly trackingUrl?: string;
  readonly courierName?: string;
  readonly courierPhone?: string;
  readonly courierPhonePin?: string;
  readonly dasherVehicle?: IOrderDasherVehicle;
  readonly deliveryAddressComponents?: IOrderDeliveryAddressComponents;
  // Test-order mode + customer milestone-SMS dedup — see IOrder JSDoc.
  readonly isTest?: boolean;
  readonly notifiedCourierMilestones?: string[];
  // Digital receipt dispatch (C3) — see IOrder JSDoc.
  readonly receiptSentAt?: Date;
  readonly receiptChannels?: string[];
  // Loyalty redemption pending burn (C2) — see IOrder JSDoc.
  readonly pendingLoyaltyBurn?: IOrderPendingLoyaltyBurn;

  // Check splitting & transfer fields
  readonly checks: (string | number)[];
  readonly activeCheckCount?: number;
  readonly totalServiceCharges: number;
  readonly totalServiceTax: number;
  readonly splitStatus: OrderSplitStatus;
  readonly hasReceivedTransfers?: boolean;
  readonly hasOutgoingTransfers?: boolean;
  readonly cancellationSubNumber?: number;
  // Uber Eats marketplace-specific ingest fields — see IOrder JSDoc.
  readonly customerPhonePin?: string;
  readonly cutleryRequested?: boolean;
  readonly deliveryNotes?: string;
  readonly marketplaceFacilitatorTaxCents?: number;
  /** The Online Service Fee line, when one was charged (its money is in totalServiceCharges/totalServiceTax). */
  readonly onlineServiceFee?: OnlineServiceFeeLine;
  /**
   * SERVER-OWNED: stamped by checkout when this order was placed under
   * OrderUp's per-order fee program (kill switch on). The ingest bills ONLY an
   * order that carries it — billing follows the checkout, never the clock at
   * close. See docs/ONLINE_SERVICE_FEE.md.
   */
  readonly onlineOrderFeeProgram?: OnlineOrderFeeProgramStamp;

  constructor(data: IOrderData) {
    this.docId = data.docId;
    this.orderId = data.orderId;
    this.orderNumber = data.orderNumber ?? data.orderId;
    this.guid = data.guid;
    this.restaurantId = data.restaurantId;
    this.tenantId = data.tenantId;
    this.customerId = data.customerId;
    this.customerName = data.customerName;
    this.customerPhone = data.customerPhone;
    this.customerEmail = data.customerEmail;
    this.smsConsent = data.smsConsent;
    this.smsConsentRecord = data.smsConsentRecord;
    this.cartId = data.cartId;
    this.items = data.items ?? [];
    this.tableId = data.tableId;
    this.activeSeatCount = data.activeSeatCount;
    this.courseStates = data.courseStates;
    this.type = data.type ?? "dineIn";
    this.orderLevelDiscounts = data.orderLevelDiscounts ?? [];
    this.appliedTaxes = data.appliedTaxes ?? [];
    this.taxExemption = data.taxExemption;
    this.waivedTax = data.waivedTax;
    this.lateAutoGratuity = data.lateAutoGratuity;
    this.scheduledTime = data.scheduledTime;
    this.fireAt = data.fireAt;
    this.fireBlocked = data.fireBlocked;
    this.stockCommit = data.stockCommit;
    this.promisedReadyAt = data.promisedReadyAt;
    this.prepQuote = data.prepQuote;
    this.deliveryAddress = data.deliveryAddress;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.createdByUserId = data.createdByUserId;
    this.tabName = data.tabName;
    this.serviceMode = data.serviceMode;
    this.customerNotes = data.customerNotes;
    this.internalNotes = data.internalNotes;
    this.synced = data.synced ?? false;
    this.canceledAt = data.canceledAt;
    this.canceledBy = data.canceledBy;
    this.voidedAt = data.voidedAt;
    this.voidedBy = data.voidedBy;
    // Three-axis state
    this.orderState = data.orderState ?? "draft";
    this.kitchenState = data.kitchenState ?? "none";
    this.closedAt = data.closedAt;
    this.reopenedAt = data.reopenedAt;
    this.reopenReason = data.reopenReason;
    this.reopenedByPaymentGuid = data.reopenedByPaymentGuid;
    this.voidInfo = data.voidInfo;
    this.voidReturnedModifierStock = data.voidReturnedModifierStock;
    this.isOverdue = data.isOverdue;
    this.syncState = data.syncState ?? "localOnly";
    this.syncPriority = data.syncPriority ?? "normal";
    this.lastSyncAttempt = data.lastSyncAttempt;
    this.syncRetryCount = data.syncRetryCount ?? 0;
    this.startedAt = data.startedAt;
    this.sentToKitchenAt = data.sentToKitchenAt;
    this.requiresFirestoreSync = data.requiresFirestoreSync ?? false;
    this.isLocalOnly = data.isLocalOnly ?? false;

    // Employee tracking
    this.createdByEmployeeId = data.createdByEmployeeId;
    this.modifiedByEmployeeId = data.modifiedByEmployeeId;
    this.sentToKitchenByEmployeeId = data.sentToKitchenByEmployeeId;
    this.completedByEmployeeId = data.completedByEmployeeId;
    this.assignedEmployeeId = data.assignedEmployeeId;
    this.assignedDeviceId = data.assignedDeviceId;
    this.supportActor = data.supportActor;
    this.supportOpener = data.supportOpener;
    this.segmentGuid = data.segmentGuid;

    // Denormalized owning tenant/restaurant GUIDs
    this.tenantGuid = data.tenantGuid;
    this.restaurantGuid = data.restaurantGuid;
    this.brandGuid = data.brandGuid;
    this.brandName = data.brandName;

    // Order source tracking
    this.orderSource = data.orderSource;
    this.attribution = data.attribution;
    this.externalOrderId = data.externalOrderId;
    this.externalSource = data.externalSource;
    this.externalDeliveryId = data.externalDeliveryId;
    this.courierProvider = data.courierProvider;
    this.customerDeliveryFeeCents = data.customerDeliveryFeeCents;
    this.driveQuoteId = data.driveQuoteId;
    this.driveQuoteExpiresAt = data.driveQuoteExpiresAt;
    this.merchantDriveCostCents = data.merchantDriveCostCents;
    this.courierQuoteId = data.courierQuoteId;
    this.courierQuoteExpiresAt = data.courierQuoteExpiresAt;
    this.merchantCourierCostCents = data.merchantCourierCostCents;
    this.courierLocation = data.courierLocation;
    this.courierDispatchFailure = data.courierDispatchFailure;
    this.courierStatus = data.courierStatus;
    this.dasherLocation = data.dasherLocation;
    this.eta = data.eta;
    this.courierStatusUpdatedAt = data.courierStatusUpdatedAt;
    this.trackingUrl = data.trackingUrl;
    this.courierName = data.courierName;
    this.courierPhone = data.courierPhone;
    this.courierPhonePin = data.courierPhonePin;
    this.dasherVehicle = data.dasherVehicle;
    this.deliveryAddressComponents = data.deliveryAddressComponents;
    this.isTest = data.isTest;
    this.notifiedCourierMilestones = data.notifiedCourierMilestones;
    // Digital receipt dispatch (C3)
    this.receiptSentAt = data.receiptSentAt;
    this.receiptChannels = data.receiptChannels;
    // Loyalty redemption pending burn (C2)
    this.pendingLoyaltyBurn = data.pendingLoyaltyBurn;

    // Check splitting & transfer fields
    this.checks = data.checks ?? [];
    this.activeCheckCount = data.activeCheckCount;
    this.totalServiceCharges = data.totalServiceCharges ?? 0;
    this.totalServiceTax = data.totalServiceTax ?? 0;
    this.splitStatus = data.splitStatus ?? 'unsplit';
    this.hasReceivedTransfers = data.hasReceivedTransfers;
    this.hasOutgoingTransfers = data.hasOutgoingTransfers;
    this.cancellationSubNumber = data.cancellationSubNumber;
    // Uber Eats marketplace-specific ingest fields
    this.customerPhonePin = data.customerPhonePin;
    this.cutleryRequested = data.cutleryRequested;
    this.deliveryNotes = data.deliveryNotes;
    this.marketplaceFacilitatorTaxCents = data.marketplaceFacilitatorTaxCents;
    this.onlineServiceFee = data.onlineServiceFee;
    this.onlineOrderFeeProgram = data.onlineOrderFeeProgram;
  }

  // Calculated getters

  /**
   * The lines that still count — voided ones struck out.
   *
   * 🔴 THE MONEY GETTERS BELOW USED TO READ `this.items`, so a voided line
   * was still sold here while the POS (`Order.activeItems` in
   * `apps/pos/lib/models/order/order.dart`) and `CheckSplitService` both
   * excluded it. Same name as the POS getter, same rule
   * ({@link isVoidedOrderItem}), so the two cannot drift again.
   *
   * NOT applied to `requiresVoiding` / `canBeCancelled`: those ask what the
   * KITCHEN committed to, and `voided` is not in `KITCHEN_COMMITTED_STATUSES`
   * anyway — filtering there would change nothing but hide the question.
   */
  get activeItems(): OrderItem[] {
    return activeOrderItems(this.items);
  }

  get subtotalBeforeDiscounts(): number {
    return this.activeItems.reduce(
      (total, item) => total + item.totalOriginalPrice,
      0,
    );
  }

  get itemLevelDiscountTotal(): number {
    // Must filter with the SAME predicate as `subtotalBeforeDiscounts`:
    // excluding a voided line's price while still subtracting its discount
    // would drive the subtotal negative on any comped-then-voided line.
    return this.activeItems.reduce(
      (total, item) => total + item.totalDiscountAmount,
      0,
    );
  }

  get subtotalAfterItemDiscounts(): number {
    return this.subtotalBeforeDiscounts - this.itemLevelDiscountTotal;
  }

  get orderLevelDiscountTotal(): number {
    return this.orderLevelDiscounts.reduce(
      (total, discount) => total + discount.amountApplied,
      0,
    );
  }

  get subtotal(): number {
    return this.subtotalAfterItemDiscounts - this.orderLevelDiscountTotal;
  }

  get totalTax(): number {
    return this.appliedTaxes.reduce(
      (total, tax) => total + tax.amountCalculated,
      0,
    );
  }

  /** Order total: subtotal + tax + service charges + service tax. */
  get total(): number {
    return this.subtotal + this.totalTax + this.totalServiceCharges + this.totalServiceTax;
  }

  get displayOrderNumber(): string {
    // 0 means NO NUMBER IS HELD. A live draft has not been given one yet
    // ("New"); a terminal order gave its back — an empty draft that was
    // cancelled returns its number to the day's pool so the next ticket draws
    // it — so it has none to show, and "New" would be a lie about a cancelled
    // ticket. Same rule as the POS (`Order.displayOrderNumber`, POS #312).
    if (this.orderNumber === 0) {
      return this.isTerminal ? "—" : "New";
    }
    if (this.cancellationSubNumber) {
      return `#${this.orderNumber}.${this.cancellationSubNumber}`;
    }
    return `#${this.orderNumber}`;
  }

  // Business logic getters
  /**
   * Whether discarding this order needs a VOID (manager approval + audit
   * trail) rather than a CANCEL (the "nothing happened yet, bin it" path).
   *
   * 🔴 `fired` WAS MISSING FROM THIS LIST — in this copy and in the Dart twin
   * (`apps/pos/lib/models/order/order.dart:710`). A course the kitchen was
   * actively cooking therefore reported `requiresVoiding: false`, so
   * `canBeCancelled` was true and `DiscardOrderUseCase` took the cancel path:
   * no approval, no audit trail, food already on the pass.
   *
   * Observed in production at House of Pho — order #2 on 2026-08-27, and
   * #22/#23/#24 on 2026-08-26, all `kitchenState: sent` and all `cancelled`.
   *
   * `fired` is unambiguously PAST `sentToKitchen`; the omission was an
   * oversight, not a decision — the original comment already read "requires
   * voiding if it has been sent to kitchen". Pinned by a parity test against
   * the Dart list, which had drifted silently.
   */
  get requiresVoiding(): boolean {
    return this.items.some((item) =>
      KITCHEN_COMMITTED_STATUSES.includes(item.kitchenStatus),
    );
  }

  get canBeCancelled(): boolean {
    return !this.requiresVoiding;
  }

  get canBeVoided(): boolean {
    return this.requiresVoiding;
  }

  get isCommittedToKitchen(): boolean {
    return ['sent', 'preparing', 'ready', 'served'].includes(this.kitchenState);
  }

  get needsFirestoreSync(): boolean {
    return (
      this.requiresFirestoreSync &&
      !this.isLocalOnly &&
      this.syncState !== "synced"
    );
  }

  get canAddItems(): boolean {
    return ['draft', 'open'].includes(this.orderState);
  }

  get canSendToKitchen(): boolean {
    return (
      this.items.length > 0 &&
      ['draft', 'open'].includes(this.orderState)
    );
  }

  get isClosed(): boolean {
    return this.orderState === 'closed';
  }

  /**
   * The order is finished and will not take another item.
   *
   * `transferred` BELONGS HERE and was missing. Three other copies of this rule
   * already agreed it is terminal and only the TypeScript one did not:
   * `apps/pos/lib/models/order/order.dart` (`isTerminal`), the Firestore rules
   * (`firestore-rules/00-legacy.rules`, "Terminal states (closed, cancelled,
   * voided, transferred)"), and `docs/ORDER_CHECK_LIFECYCLE_SEMANTICS.md` §
   * "`Order.isTerminal` = `closed | cancelled | voided | transferred`".
   *
   * A transferred order has had its items moved to another order; treating it
   * as OPEN server-side means accepting an append the rules reject and the till
   * has already walked away from. That mattered little while nothing in TS read
   * this getter, and it stops being harmless the moment a guest round is
   * pre-validated against it (Guest QR Platform piece 5, spec §6.6) — the
   * server would clear a round the till then refuses, and the guest would be
   * told their drinks were ordered.
   *
   * `ORDER_TERMINAL_STATES` is exported and asserted against the Dart source by
   * `orderTerminalParity.test.ts`, so the next divergence fails a test instead
   * of being discovered by a restaurant.
   */
  get isTerminal(): boolean {
    return (ORDER_TERMINAL_STATES as readonly string[]).includes(this.orderState);
  }

  get displayStatus(): string {
    switch (this.orderState) {
      case "draft":
        return "Building Order";
      case "pendingAcceptance":
        return "Pending Acceptance";
      case "scheduled":
        return "Scheduled";
      case "open":
        return "Open";
      case "held":
        return "Held";
      case "closed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      case "voided":
        return "Voided";
      // Checkout-held: never rendered by a staff surface (they are filtered
      // out), named so a stray render says what it is rather than "Unknown".
      case "awaitingPayment":
        return "Awaiting Payment";
      case "abandoned":
        return "Checkout Abandoned";
      default:
        return "Unknown";
    }
  }

  get syncStatusDisplay(): string {
    switch (this.syncState) {
      case "localOnly":
        return "Local Only";
      case "pendingSync":
        return "Pending Sync";
      case "syncing":
        return "Syncing...";
      case "synced":
        return "Synced";
      case "syncFailed":
        return "Sync Failed";
      case "conflicted":
        return "Conflict";
      default:
        return "Unknown";
    }
  }

  toJSON(): IOrder {
    return {
      docId: this.docId,
      orderId: this.orderId,
      orderNumber: this.orderNumber,
      guid: this.guid,
      restaurantId: this.restaurantId,
      tenantId: this.tenantId,
      customerId: this.customerId,
      customerName: this.customerName,
      customerPhone: this.customerPhone,
      customerEmail: this.customerEmail,
      smsConsent: this.smsConsent,
      smsConsentRecord: this.smsConsentRecord,
      cartId: this.cartId,
      items: this.items.map((item) => item.toJSON()),
      tableId: this.tableId,
      activeSeatCount: this.activeSeatCount,
      courseStates: this.courseStates,
      type: this.type,
      orderLevelDiscounts: this.orderLevelDiscounts.map((d) => d.toJSON()),
      appliedTaxes: this.appliedTaxes.map((t) => t.toJSON()),
      taxExemption: this.taxExemption,
      waivedTax: this.waivedTax,
      lateAutoGratuity: this.lateAutoGratuity,
      scheduledTime: this.scheduledTime,
      fireAt: this.fireAt,
      fireBlocked: this.fireBlocked,
      stockCommit: this.stockCommit,
      promisedReadyAt: this.promisedReadyAt,
      prepQuote: this.prepQuote,
      deliveryAddress: this.deliveryAddress,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      createdByUserId: this.createdByUserId,
      tabName: this.tabName,
      // Only when set: a legacy order serialises exactly as before.
      ...(this.serviceMode !== undefined ? { serviceMode: this.serviceMode } : {}),
      customerNotes: this.customerNotes,
      internalNotes: this.internalNotes,
      synced: this.synced,
      canceledAt: this.canceledAt,
      canceledBy: this.canceledBy,
      voidedAt: this.voidedAt,
      voidedBy: this.voidedBy,
      orderState: this.orderState,
      kitchenState: this.kitchenState,
      closedAt: this.closedAt,
      reopenedAt: this.reopenedAt,
      reopenReason: this.reopenReason,
      reopenedByPaymentGuid: this.reopenedByPaymentGuid,
      voidInfo: this.voidInfo,
      voidReturnedModifierStock: this.voidReturnedModifierStock,
      isOverdue: this.isOverdue,
      syncState: this.syncState,
      syncPriority: this.syncPriority,
      lastSyncAttempt: this.lastSyncAttempt,
      syncRetryCount: this.syncRetryCount,
      startedAt: this.startedAt,
      sentToKitchenAt: this.sentToKitchenAt,
      requiresFirestoreSync: this.requiresFirestoreSync,
      isLocalOnly: this.isLocalOnly,
      // Employee tracking
      createdByEmployeeId: this.createdByEmployeeId,
      modifiedByEmployeeId: this.modifiedByEmployeeId,
      sentToKitchenByEmployeeId: this.sentToKitchenByEmployeeId,
      completedByEmployeeId: this.completedByEmployeeId,
      assignedEmployeeId: this.assignedEmployeeId,
      assignedDeviceId: this.assignedDeviceId,
      supportActor: this.supportActor,
      supportOpener: this.supportOpener,
      segmentGuid: this.segmentGuid,
      // Denormalized owning tenant/restaurant GUIDs
      tenantGuid: this.tenantGuid,
      restaurantGuid: this.restaurantGuid,
      brandGuid: this.brandGuid,
      brandName: this.brandName,
      // Order source tracking
      orderSource: this.orderSource,
      attribution: this.attribution,
      externalOrderId: this.externalOrderId,
      externalSource: this.externalSource,
      externalDeliveryId: this.externalDeliveryId,
      courierProvider: this.courierProvider,
      customerDeliveryFeeCents: this.customerDeliveryFeeCents,
      driveQuoteId: this.driveQuoteId,
      driveQuoteExpiresAt: this.driveQuoteExpiresAt,
      merchantDriveCostCents: this.merchantDriveCostCents,
      courierQuoteId: this.courierQuoteId,
      courierQuoteExpiresAt: this.courierQuoteExpiresAt,
      merchantCourierCostCents: this.merchantCourierCostCents,
      courierLocation: this.courierLocation,
      courierDispatchFailure: this.courierDispatchFailure,
      courierStatus: this.courierStatus,
      dasherLocation: this.dasherLocation,
      eta: this.eta,
      courierStatusUpdatedAt: this.courierStatusUpdatedAt,
      trackingUrl: this.trackingUrl,
      courierName: this.courierName,
      courierPhone: this.courierPhone,
      courierPhonePin: this.courierPhonePin,
      dasherVehicle: this.dasherVehicle,
      deliveryAddressComponents: this.deliveryAddressComponents,
      isTest: this.isTest,
      notifiedCourierMilestones: this.notifiedCourierMilestones,
      // Digital receipt dispatch (C3)
      receiptSentAt: this.receiptSentAt,
      receiptChannels: this.receiptChannels,
      // Loyalty redemption pending burn (C2)
      pendingLoyaltyBurn: this.pendingLoyaltyBurn,
      // Check splitting & transfer fields
      checks: this.checks,
      activeCheckCount: this.activeCheckCount,
      totalServiceCharges: this.totalServiceCharges,
      totalServiceTax: this.totalServiceTax,
      splitStatus: this.splitStatus,
      hasReceivedTransfers: this.hasReceivedTransfers,
      hasOutgoingTransfers: this.hasOutgoingTransfers,
      cancellationSubNumber: this.cancellationSubNumber,
      // Uber Eats marketplace-specific ingest fields
      customerPhonePin: this.customerPhonePin,
      cutleryRequested: this.cutleryRequested,
      deliveryNotes: this.deliveryNotes,
      marketplaceFacilitatorTaxCents: this.marketplaceFacilitatorTaxCents,
      onlineServiceFee: this.onlineServiceFee,
      onlineOrderFeeProgram: this.onlineOrderFeeProgram,
    };
  }

  static fromJSON(data: Record<string, unknown>): Order {
    // Helper to coerce to number (handles both numbers and numeric strings)
    const toNumber = (value: unknown, fieldName: string): number => {
      if (typeof value === "number") return value;
      if (typeof value === "string") {
        const num = Number(value);
        if (!isNaN(num)) return num;
      }
      throw new Error(
        `Order.fromJSON: ${fieldName} must be a number (got ${typeof value}: ${value})`,
      );
    };

    // Helper for optional numbers with warning
    const toNumberWithDefault = (
      value: unknown,
      fieldName: string,
      defaultValue: number
    ): number => {
      if (value === undefined || value === null) {
        if (typeof console !== 'undefined') {
          console.warn(
            `Order.fromJSON: ${fieldName} is missing (docId: ${data.docId}). Using default: ${defaultValue}. Run data migration to fix.`
          );
        }
        return defaultValue;
      }
      return toNumber(value, fieldName);
    };

    const orderId = toNumber(data.orderId, "orderId");
    const locationNumericId = toNumberWithDefault(data.restaurantId, "restaurantId", 0);
    const tenantId = toNumberWithDefault(data.tenantId, "tenantId", 0);

    if (typeof data.guid !== "string") {
      throw new Error("Order.fromJSON: guid must be a string");
    }
    if (typeof data.createdByUserId !== "string") {
      throw new Error("Order.fromJSON: createdByUserId must be a string");
    }

    const parseDate = (value: unknown): Date | undefined => {
      if (value instanceof Date) return value;
      if (typeof value === "string") return new Date(value);
      // Handle Firestore Timestamp objects
      if (
        value &&
        typeof value === "object" &&
        "toDate" in value &&
        typeof (value as any).toDate === "function"
      ) {
        return (value as any).toDate();
      }
      return undefined;
    };

    /**
     * Firestore hands `firedAt` back as a Timestamp, and a document written
     * before this field existed can hold anything at all. `IOrderCourseState`
     * declares a `Date`, so casting the map through untouched is a typed lie
     * that only detonates at the call site formatting the fire ticket.
     *
     * An unreadable `state` resolves to `fire` for the same reason
     * `toCourseFireMode` resolves to `immediate`: the recoverable failure is a
     * dish leaving the pass early, not a dish nobody ever makes.
     */
    const parseCourseStates = (
      value: unknown,
    ): Record<string, IOrderCourseState> | undefined => {
      if (!value || typeof value !== "object") return undefined;
      const states: Record<string, IOrderCourseState> = {};
      for (const [courseId, raw] of Object.entries(
        value as Record<string, unknown>,
      )) {
        if (!raw || typeof raw !== "object") continue;
        const entry = raw as Record<string, unknown>;
        const firedAt = parseDate(entry.firedAt);
        states[courseId] = {
          state: entry.state === "hold" ? "hold" : "fire",
          ...(firedAt ? { firedAt } : {}),
          ...(typeof entry.firedBy === "string"
            ? { firedBy: entry.firedBy }
            : {}),
        };
      }
      return states;
    };

    const items = Array.isArray(data.items)
      ? data.items.map((item: any) => OrderItem.fromJSON(item))
      : [];

    const orderLevelDiscounts = Array.isArray(data.orderLevelDiscounts)
      ? data.orderLevelDiscounts.map((d: any) => AppliedDiscount.fromJSON(d))
      : [];

    const appliedTaxes = Array.isArray(data.appliedTaxes)
      ? data.appliedTaxes.map((t: any) => AppliedTax.fromJSON(t))
      : [];

    return new Order({
      docId: data.docId as string | undefined,
      orderId: orderId,
      orderNumber: parseOrderNumber(data.orderNumber, orderId),
      guid: data.guid,
      restaurantId: locationNumericId,
      tenantId: tenantId,
      customerId: data.customerId as string | undefined,
      customerName: data.customerName as string | undefined,
      customerPhone: data.customerPhone as string | undefined,
      customerEmail: data.customerEmail as string | undefined,
      // Read, never dropped: every SMS sender gates on this, and a parse that
      // loses it reads every order as "never asked" — which under the
      // `=== true` rule silences an online guest who DID opt in, and under
      // the old `!== false` rule texted one who declined.
      smsConsent:
        typeof data.smsConsent === "boolean" ? data.smsConsent : undefined,
      smsConsentRecord: parseSmsConsentRecord(data.smsConsentRecord),
      cartId: data.cartId as number | undefined,
      items,
      tableId: data.tableId as string | undefined,
      activeSeatCount: data.activeSeatCount as number | undefined,
      courseStates: parseCourseStates(data.courseStates),
      type: (data.type as OrderType) ?? "dineIn",
      orderLevelDiscounts,
      appliedTaxes,
      // Defensive: a malformed map reads as absent rather than failing the
      // whole order parse (see TaxExemption.ts).
      taxExemption: parseTaxExemption(data.taxExemption),
      waivedTax: parseWaivedTax(data.waivedTax),
      lateAutoGratuity: parseLateAutoGratuity(data.lateAutoGratuity),
      scheduledTime: parseDate(data.scheduledTime),
      fireAt: parseDate(data.fireAt),
      fireBlocked: parseHeldOrderFireBlock(data.fireBlocked),
      stockCommit: data.stockCommit === STOCK_COMMIT_AT_SEND ? STOCK_COMMIT_AT_SEND : undefined,
      promisedReadyAt: parseDate(data.promisedReadyAt),
      prepQuote: data.prepQuote as PrepTimeQuote | undefined,
      deliveryAddress: data.deliveryAddress as string | undefined,
      createdAt: parseDate(data.createdAt),
      updatedAt: parseDate(data.updatedAt),
      createdByUserId: data.createdByUserId,
      tabName: data.tabName as string | undefined,
      serviceMode: parseOrderServiceMode(data.serviceMode),
      customerNotes: data.customerNotes as string | undefined,
      internalNotes: data.internalNotes as string | undefined,
      synced: (data.synced as boolean) ?? false,
      canceledAt: parseDate(data.canceledAt),
      canceledBy: data.canceledBy as string | undefined,
      voidedAt: parseDate(data.voidedAt),
      voidedBy: data.voidedBy as string | undefined,
      orderState: data.orderState as OrderState | undefined,
      kitchenState: data.kitchenState as KitchenState | undefined,
      closedAt: parseDate(data.closedAt),
      reopenedAt: parseDate(data.reopenedAt),
      reopenReason: data.reopenReason === "payment_voided" ? "payment_voided" : undefined,
      reopenedByPaymentGuid: data.reopenedByPaymentGuid as string | undefined,
      voidInfo: data.voidInfo ? {
        reason: (data.voidInfo as any).reason as string,
        authorizedBy: (data.voidInfo as any).authorizedBy as string,
        ...readWriteOffActors(data.voidInfo),
      } : undefined,
      voidReturnedModifierStock: parseModifierStockReturns(data.voidReturnedModifierStock),
      isOverdue: data.isOverdue as boolean | undefined,
      syncState: (data.syncState as OrderSyncState) ?? "localOnly",
      syncPriority: (data.syncPriority as OrderSyncPriority) ?? "normal",
      lastSyncAttempt: parseDate(data.lastSyncAttempt),
      syncRetryCount: (data.syncRetryCount as number) ?? 0,
      startedAt: parseDate(data.startedAt),
      sentToKitchenAt: parseDate(data.sentToKitchenAt),
      requiresFirestoreSync: (data.requiresFirestoreSync as boolean) ?? false,
      isLocalOnly: (data.isLocalOnly as boolean) ?? false,
      // Employee tracking
      createdByEmployeeId: data.createdByEmployeeId as number | undefined,
      modifiedByEmployeeId: data.modifiedByEmployeeId as number | undefined,
      sentToKitchenByEmployeeId: data.sentToKitchenByEmployeeId as number | undefined,
      completedByEmployeeId: data.completedByEmployeeId as number | undefined,
      assignedEmployeeId: data.assignedEmployeeId as number | undefined,
      assignedDeviceId: data.assignedDeviceId as string | undefined,
      supportActor: parseSupportActor(data.supportActor),
      supportOpener: isSupportActingAs(data.supportOpener) ? data.supportOpener : undefined,
      segmentGuid: data.segmentGuid as string | undefined,
      // Denormalized owning tenant/restaurant GUIDs
      tenantGuid: data.tenantGuid as string | undefined,
      restaurantGuid: data.restaurantGuid as string | undefined,
      brandGuid: data.brandGuid as string | undefined,
      brandName: data.brandName as string | undefined,
      // Order source tracking
      orderSource: data.orderSource as OrderSource | undefined,
      attribution:
        data.attribution && typeof data.attribution === "object"
          ? (data.attribution as IOrderAttribution)
          : undefined,
      externalOrderId: data.externalOrderId as string | undefined,
      externalSource: data.externalSource as ExternalOrderSource | undefined,
      externalDeliveryId: data.externalDeliveryId as string | undefined,
      courierProvider: data.courierProvider as DeliveryProviderKey | undefined,
      customerDeliveryFeeCents: data.customerDeliveryFeeCents as number | undefined,
      driveQuoteId: data.driveQuoteId as string | undefined,
      driveQuoteExpiresAt: data.driveQuoteExpiresAt as string | undefined,
      merchantDriveCostCents: data.merchantDriveCostCents as number | undefined,
      courierQuoteId: data.courierQuoteId as string | undefined,
      courierQuoteExpiresAt: data.courierQuoteExpiresAt as string | undefined,
      merchantCourierCostCents: data.merchantCourierCostCents as number | undefined,
      courierLocation: data.courierLocation as IOrderGeoPoint | undefined,
      courierDispatchFailure: data.courierDispatchFailure as IOrderCourierDispatchFailure | undefined,
      courierStatus: data.courierStatus as string | undefined,
      dasherLocation: data.dasherLocation as { lat: number; lng: number } | undefined,
      eta: data.eta as string | undefined,
      courierStatusUpdatedAt: data.courierStatusUpdatedAt as string | undefined,
      trackingUrl: data.trackingUrl as string | undefined,
      courierName: data.courierName as string | undefined,
      courierPhone: data.courierPhone as string | undefined,
      courierPhonePin: data.courierPhonePin as string | undefined,
      dasherVehicle: data.dasherVehicle as IOrderDasherVehicle | undefined,
      deliveryAddressComponents: data.deliveryAddressComponents as
        | IOrderDeliveryAddressComponents
        | undefined,
      isTest: data.isTest as boolean | undefined,
      notifiedCourierMilestones: Array.isArray(data.notifiedCourierMilestones)
        ? (data.notifiedCourierMilestones as string[])
        : undefined,
      // Digital receipt dispatch (C3) — Date round-trips a Firestore Timestamp
      // (mirrors closedAt); channels is a string[] (mirrors notifiedCourierMilestones).
      receiptSentAt: parseDate(data.receiptSentAt),
      receiptChannels: Array.isArray(data.receiptChannels)
        ? (data.receiptChannels as string[])
        : undefined,
      // Loyalty redemption pending burn (C2) — guarded object parse (mirrors
      // voidInfo); a cleared marker is persisted as null → parsed back to undefined.
      pendingLoyaltyBurn:
        data.pendingLoyaltyBurn && typeof data.pendingLoyaltyBurn === "object"
          ? {
              customerUid: (data.pendingLoyaltyBurn as IOrderPendingLoyaltyBurn)
                .customerUid,
              points: (data.pendingLoyaltyBurn as IOrderPendingLoyaltyBurn).points,
            }
          : undefined,
      // Check splitting & transfer fields — reference array (checkId or checkGuid)
      checks: Array.isArray(data.checks)
        ? (data.checks as (string | number)[])
        : [],
      activeCheckCount: data.activeCheckCount as number | undefined,
      totalServiceCharges: data.totalServiceCharges as number | undefined,
      totalServiceTax: data.totalServiceTax as number | undefined,
      splitStatus: (data.splitStatus as OrderSplitStatus) ?? 'unsplit',
      hasReceivedTransfers: data.hasReceivedTransfers as boolean | undefined,
      hasOutgoingTransfers: data.hasOutgoingTransfers as boolean | undefined,
      cancellationSubNumber: data.cancellationSubNumber as number | undefined,
      // Uber Eats marketplace-specific ingest fields
      customerPhonePin: data.customerPhonePin as string | undefined,
      cutleryRequested: data.cutleryRequested as boolean | undefined,
      deliveryNotes: data.deliveryNotes as string | undefined,
      marketplaceFacilitatorTaxCents: data.marketplaceFacilitatorTaxCents as number | undefined,
      onlineServiceFee: data.onlineServiceFee as OnlineServiceFeeLine | undefined,
      onlineOrderFeeProgram: parseOnlineOrderFeeProgram(data.onlineOrderFeeProgram),
    });
  }

  copyWith(data: Partial<IOrderData>): Order {
    return new Order({
      docId: data.docId ?? this.docId,
      orderId: data.orderId ?? this.orderId,
      orderNumber: data.orderNumber ?? this.orderNumber,
      guid: data.guid ?? this.guid,
      restaurantId: data.restaurantId ?? this.restaurantId,
      tenantId: data.tenantId ?? this.tenantId,
      customerId: data.customerId ?? this.customerId,
      customerName: data.customerName ?? this.customerName,
      customerPhone: data.customerPhone ?? this.customerPhone,
      customerEmail: data.customerEmail ?? this.customerEmail,
      smsConsent: data.smsConsent ?? this.smsConsent,
      smsConsentRecord: data.smsConsentRecord ?? this.smsConsentRecord,
      cartId: data.cartId ?? this.cartId,
      items: data.items ?? this.items,
      tableId: data.tableId ?? this.tableId,
      activeSeatCount: data.activeSeatCount ?? this.activeSeatCount,
      courseStates: data.courseStates ?? this.courseStates,
      type: data.type ?? this.type,
      orderLevelDiscounts: data.orderLevelDiscounts ?? this.orderLevelDiscounts,
      appliedTaxes: data.appliedTaxes ?? this.appliedTaxes,
      taxExemption: data.taxExemption ?? this.taxExemption,
      waivedTax: data.waivedTax ?? this.waivedTax,
      lateAutoGratuity: data.lateAutoGratuity ?? this.lateAutoGratuity,
      scheduledTime: data.scheduledTime ?? this.scheduledTime,
      fireAt: data.fireAt ?? this.fireAt,
      fireBlocked: data.fireBlocked !== undefined ? data.fireBlocked : this.fireBlocked,
      stockCommit: data.stockCommit ?? this.stockCommit,
      promisedReadyAt: data.promisedReadyAt ?? this.promisedReadyAt,
      prepQuote: data.prepQuote ?? this.prepQuote,
      deliveryAddress: data.deliveryAddress ?? this.deliveryAddress,
      createdAt: data.createdAt ?? this.createdAt,
      updatedAt: data.updatedAt ?? this.updatedAt,
      createdByUserId: data.createdByUserId ?? this.createdByUserId,
      tabName: data.tabName ?? this.tabName,
      serviceMode: data.serviceMode ?? this.serviceMode,
      customerNotes: data.customerNotes ?? this.customerNotes,
      internalNotes: data.internalNotes ?? this.internalNotes,
      synced: data.synced ?? this.synced,
      canceledAt: data.canceledAt ?? this.canceledAt,
      canceledBy: data.canceledBy ?? this.canceledBy,
      voidedAt: data.voidedAt ?? this.voidedAt,
      voidedBy: data.voidedBy ?? this.voidedBy,
      orderState: data.orderState ?? this.orderState,
      kitchenState: data.kitchenState ?? this.kitchenState,
      closedAt: data.closedAt ?? this.closedAt,
      reopenedAt: data.reopenedAt ?? this.reopenedAt,
      reopenReason: data.reopenReason ?? this.reopenReason,
      reopenedByPaymentGuid: data.reopenedByPaymentGuid ?? this.reopenedByPaymentGuid,
      voidInfo: data.voidInfo ?? this.voidInfo,
      isOverdue: data.isOverdue ?? this.isOverdue,
      syncState: data.syncState ?? this.syncState,
      syncPriority: data.syncPriority ?? this.syncPriority,
      lastSyncAttempt: data.lastSyncAttempt ?? this.lastSyncAttempt,
      syncRetryCount: data.syncRetryCount ?? this.syncRetryCount,
      startedAt: data.startedAt ?? this.startedAt,
      sentToKitchenAt: data.sentToKitchenAt ?? this.sentToKitchenAt,
      requiresFirestoreSync:
        data.requiresFirestoreSync ?? this.requiresFirestoreSync,
      isLocalOnly: data.isLocalOnly ?? this.isLocalOnly,
      // Employee tracking
      createdByEmployeeId: data.createdByEmployeeId ?? this.createdByEmployeeId,
      modifiedByEmployeeId: data.modifiedByEmployeeId ?? this.modifiedByEmployeeId,
      sentToKitchenByEmployeeId: data.sentToKitchenByEmployeeId ?? this.sentToKitchenByEmployeeId,
      completedByEmployeeId: data.completedByEmployeeId ?? this.completedByEmployeeId,
      assignedEmployeeId: data.assignedEmployeeId ?? this.assignedEmployeeId,
      assignedDeviceId: data.assignedDeviceId ?? this.assignedDeviceId,
      supportActor: data.supportActor ?? this.supportActor,
      // Immutable after create: an update never replaces it.
      supportOpener: this.supportOpener,
      segmentGuid: data.segmentGuid ?? this.segmentGuid,
      // Denormalized owning tenant/restaurant GUIDs
      tenantGuid: data.tenantGuid ?? this.tenantGuid,
      restaurantGuid: data.restaurantGuid ?? this.restaurantGuid,
      brandGuid: data.brandGuid ?? this.brandGuid,
      brandName: data.brandName ?? this.brandName,
      // Order source tracking
      orderSource: data.orderSource ?? this.orderSource,
      attribution: data.attribution ?? this.attribution,
      externalOrderId: data.externalOrderId ?? this.externalOrderId,
      externalSource: data.externalSource ?? this.externalSource,
      externalDeliveryId: data.externalDeliveryId ?? this.externalDeliveryId,
      courierProvider: data.courierProvider ?? this.courierProvider,
      customerDeliveryFeeCents: data.customerDeliveryFeeCents ?? this.customerDeliveryFeeCents,
      driveQuoteId: data.driveQuoteId ?? this.driveQuoteId,
      driveQuoteExpiresAt: data.driveQuoteExpiresAt ?? this.driveQuoteExpiresAt,
      merchantDriveCostCents: data.merchantDriveCostCents ?? this.merchantDriveCostCents,
      courierQuoteId: data.courierQuoteId ?? this.courierQuoteId,
      courierQuoteExpiresAt: data.courierQuoteExpiresAt ?? this.courierQuoteExpiresAt,
      merchantCourierCostCents: data.merchantCourierCostCents ?? this.merchantCourierCostCents,
      courierLocation: data.courierLocation ?? this.courierLocation,
      courierDispatchFailure: data.courierDispatchFailure ?? this.courierDispatchFailure,
      courierStatus: data.courierStatus ?? this.courierStatus,
      dasherLocation: data.dasherLocation ?? this.dasherLocation,
      eta: data.eta ?? this.eta,
      courierStatusUpdatedAt: data.courierStatusUpdatedAt ?? this.courierStatusUpdatedAt,
      trackingUrl: data.trackingUrl ?? this.trackingUrl,
      courierName: data.courierName ?? this.courierName,
      courierPhone: data.courierPhone ?? this.courierPhone,
      courierPhonePin: data.courierPhonePin ?? this.courierPhonePin,
      dasherVehicle: data.dasherVehicle ?? this.dasherVehicle,
      deliveryAddressComponents:
        data.deliveryAddressComponents ?? this.deliveryAddressComponents,
      isTest: data.isTest ?? this.isTest,
      notifiedCourierMilestones:
        data.notifiedCourierMilestones ?? this.notifiedCourierMilestones,
      // Digital receipt dispatch (C3)
      receiptSentAt: data.receiptSentAt ?? this.receiptSentAt,
      receiptChannels: data.receiptChannels ?? this.receiptChannels,
      // Loyalty redemption pending burn (C2)
      pendingLoyaltyBurn: data.pendingLoyaltyBurn ?? this.pendingLoyaltyBurn,
      // Check splitting & transfer fields
      checks: data.checks ?? this.checks,
      activeCheckCount: data.activeCheckCount ?? this.activeCheckCount,
      totalServiceCharges: data.totalServiceCharges ?? this.totalServiceCharges,
      totalServiceTax: data.totalServiceTax ?? this.totalServiceTax,
      splitStatus: data.splitStatus ?? this.splitStatus,
      hasReceivedTransfers: data.hasReceivedTransfers ?? this.hasReceivedTransfers,
      hasOutgoingTransfers: data.hasOutgoingTransfers ?? this.hasOutgoingTransfers,
      cancellationSubNumber: data.cancellationSubNumber ?? this.cancellationSubNumber,
      // Uber Eats marketplace-specific ingest fields
      customerPhonePin: data.customerPhonePin ?? this.customerPhonePin,
      cutleryRequested: data.cutleryRequested ?? this.cutleryRequested,
      deliveryNotes: data.deliveryNotes ?? this.deliveryNotes,
      marketplaceFacilitatorTaxCents: data.marketplaceFacilitatorTaxCents ?? this.marketplaceFacilitatorTaxCents,
      onlineServiceFee: data.onlineServiceFee ?? this.onlineServiceFee,
      onlineOrderFeeProgram: data.onlineOrderFeeProgram ?? this.onlineOrderFeeProgram,
    });
  }

  toString(): string {
    return `Order(${this.displayOrderNumber}, items: ${this.items.length}, total: $${this.total.toFixed(2)}, status: ${this.displayStatus})`;
  }
}
